import React, { useState } from 'react';
import { ShoppingCart, TrendingUp, Users, UserCheck, MessageSquare, Award, ArrowUpRight, Play, Brain, ShieldCheck, FileText, Lightbulb, Zap, Globe, Server, Search, Sparkles, LineChart } from 'lucide-react';
import CrmPresentationModal from './CrmPresentationModal';
import AnimatedGradientText from './AnimatedGradientText';
import { useLang } from './LanguageProvider';
import translations from '../translations';

/* İKON DİZİLERİ, SÖZLÜKTEKİ MADDE SIRASIYLA BİREBİR EŞLEŞİR.
   `ProductCard` maddeleri `featureIcons[productKey][i]` ile çiziyor;
   sıra kayarsa ikon yanlış maddeye düşer, dizi kısa kalırsa `Icon`
   `undefined` olur ve render ÇÖKER. Sözlükte madde eklenirse buraya da
   ikon eklenmek zorunda.

   `app` anahtarı KALDIRILDI: "WhowSell App" diye bir ürün yok, o kart
   bulanık bir "yakında" iskeletiydi. Yerine gerçek ve CANLI olan
   `radar` geldi (radar.whowsell.com). */
const featureIcons = {
  crm: [ShoppingCart, TrendingUp, Users, UserCheck],
  radar: [Search, Sparkles, ShieldCheck, LineChart],
  ai: [Brain, ShieldCheck, FileText, Lightbulb],
  cloud: [Zap, Globe, ShieldCheck, Award],
};

const colorMap = {
  cyan: { bg: 'rgba(6, 182, 212, 0.15)', border: 'rgba(6, 182, 212, 0.35)', text: 'rgb(6, 182, 212)', hex: '#06b6d4' },
  blue: { bg: 'rgba(59, 130, 246, 0.15)', border: 'rgba(59, 130, 246, 0.2)', text: 'rgb(59, 130, 246)', hex: '#3b82f6' },
  purple: { bg: 'rgba(139, 92, 246, 0.15)', border: 'rgba(139, 92, 246, 0.2)', text: 'rgb(139, 92, 246)', hex: '#8b5cf6' },
  aiGradient: { bg: 'rgba(168, 85, 247, 0.15)', border: 'rgba(168, 85, 247, 0.35)', text: 'rgb(168, 85, 247)', hex: '#a855f7', hex2: '#ec4899' },
  /* Radar'ın kendi marka gradyanı (radar-whowsell/app/globals.css):
     #7c3aed → #d946ef. Suspend AI'ın #a855f7 → #ec4899 gradyanından
     BİLEREK daha derin bir mor seçildi: iki kart yan yana duruyor ve
     yakın morlar birbirinden ayrışmazdı. */
  radarGradient: { bg: 'rgba(124, 58, 237, 0.15)', border: 'rgba(124, 58, 237, 0.35)', text: 'rgb(139, 92, 246)', hex: '#7c3aed', hex2: '#d946ef' },
  cloudGradient: { bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.4)', text: 'rgb(16, 185, 129)', hex: '#10b981', hex2: '#06b6d4' },
};

function ProductCard({ productKey, color, status, t, onShowPresentation, link }) {
  const isLive = status === 'live';
  const isComing = status === 'coming';
  const isBeta = status === 'beta';
  const colors = colorMap[color];
  const pt = t[productKey];
  const icons = featureIcons[productKey];
  const feats = [pt.f1, pt.f2, pt.f3, pt.f4];
  const hasGradient = !!colors.hex2;

  const cardBorder = isBeta
    ? `1px solid ${colors.border}`
    : isLive
      ? '1px solid rgba(6, 182, 212, 0.35)'
      : undefined;

  return (
    <div
      data-testid={`products-${productKey}-card`}
      className={`relative rounded-2xl overflow-hidden group glow-card flex flex-col h-full ${isComing ? 'opacity-85' : ''}`}
      style={{ '--glow-color': hasGradient ? `${colors.hex}50` : `${colors.hex}60`, border: cardBorder }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="absolute top-0 left-0 right-0 h-32 pointer-events-none" style={{ background: hasGradient ? `linear-gradient(to bottom, ${colors.hex}20, ${colors.hex2}08, transparent)` : `linear-gradient(to bottom, ${colors.hex}20, ${colors.hex}05, transparent)` }} />
      <div className="relative p-6 sm:p-7 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-5">
          {isLive && (
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: colors.text, boxShadow: `${colors.text} 0px 0px 8px` }} />
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: colors.text }}>{t.live}</span>
            </div>
          )}
          {isBeta && (
            <div className="flex items-center gap-1.5">
              <Brain className="w-3 h-3" style={{ color: colors.hex2 || colors.text }} />
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ background: `linear-gradient(135deg, ${colors.hex}, ${colors.hex2 || colors.hex})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{t.beta}</span>
            </div>
          )}
          {isComing && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider dark:bg-white/10 bg-slate-200/80 dark:text-white/60 text-slate-500">{t.coming}</span>
          )}
        </div>

        {isComing ? (
          <div className="coming-soon-blur" aria-hidden="true">
            <div className="mb-2.5" style={{ height: 24, width: '65%', borderRadius: 6, background: `linear-gradient(90deg, ${colors.hex}18, ${colors.hex}08)` }} />
            <div className="mb-1.5" style={{ height: 12, width: '90%', borderRadius: 4, background: `linear-gradient(90deg, ${colors.hex}10, ${colors.hex}05)` }} />
            <div className="mb-6" style={{ height: 12, width: '70%', borderRadius: 4, background: `linear-gradient(90deg, ${colors.hex}10, ${colors.hex}05)` }} />
            <div className="space-y-3 mb-6">
              {[0.85, 0.7, 0.9, 0.6].map((w, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl shrink-0" style={{ background: colors.bg, border: `1px solid ${colors.border}` }} />
                  <div style={{ height: 12, width: `${w * 100}%`, borderRadius: 4, background: `linear-gradient(90deg, ${colors.hex}12, ${colors.hex}06)` }} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div>
            <h3 className="text-xl font-bold mb-2.5 tracking-tight dark:text-white text-slate-900">{pt.title}</h3>
            <p className="text-sm dark:text-white/55 text-slate-500 leading-relaxed mb-6">{pt.desc}</p>
            <ul className="space-y-3 mb-6">
              {feats.map((feat, i) => {
                const Icon = icons[i];
                return (
                  <li key={i} className="flex items-center gap-3 text-sm dark:text-white/70 text-slate-600">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: colors.bg, border: `1px solid ${colors.border}` }}>
                      <Icon className="w-3.5 h-3.5" style={{ color: colors.text }} />
                    </div>
                    <span className="font-medium">{feat}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {isLive && pt.cta && (
          <div className="relative z-10 flex flex-wrap items-center gap-3 mt-auto pt-2">
            <a href={link} target="_blank" rel="noopener noreferrer"
              className="wh-cta transition-all duration-300 active:scale-[0.97] hover:scale-[1.02] cursor-pointer select-none"
              style={{
                background: `linear-gradient(135deg, ${colors.hex}20, ${colors.hex}10)`,
                color: colors.text,
                border: `1.5px solid ${colors.border}`,
                boxShadow: `0 0 20px ${colors.hex}15, 0 4px 12px rgba(0,0,0,0.1)`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.hex}35, ${colors.hex}20)`;
                e.currentTarget.style.boxShadow = `0 0 30px ${colors.hex}30, 0 6px 20px rgba(0,0,0,0.15)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.hex}20, ${colors.hex}10)`;
                e.currentTarget.style.boxShadow = `0 0 20px ${colors.hex}15, 0 4px 12px rgba(0,0,0,0.1)`;
              }}
            >
              {pt.cta}
              <ArrowUpRight className="w-4 h-4" />
            </a>
            {onShowPresentation && (
              <button
                onClick={onShowPresentation}
                className="wh-cta transition-all duration-300 active:scale-[0.97] hover:scale-[1.02] cursor-pointer select-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(139,92,246,0.08))',
                  color: '#a78bfa',
                  border: '1.5px solid rgba(139,92,246,0.3)',
                  boxShadow: '0 0 20px rgba(139,92,246,0.1), 0 4px 12px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139,92,246,0.25), rgba(139,92,246,0.15))';
                  e.currentTarget.style.boxShadow = '0 0 30px rgba(139,92,246,0.25), 0 6px 20px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, rgba(139,92,246,0.15), rgba(139,92,246,0.08))';
                  e.currentTarget.style.boxShadow = '0 0 20px rgba(139,92,246,0.1), 0 4px 12px rgba(0,0,0,0.1)';
                }}
              >
                <Play className="w-4 h-4" />
                {pt.preview || 'Yakından Tanıyın'}
              </button>
            )}
          </div>
        )}

        {isBeta && pt.cta && (
          <div className="relative z-10 mt-auto pt-2">
            <a href={link} target="_blank" rel="noopener noreferrer"
              className="wh-cta transition-all duration-300 active:scale-[0.97] hover:scale-[1.02] cursor-pointer select-none"
              style={{
                background: `linear-gradient(135deg, ${colors.hex}20, ${colors.hex2 || colors.hex}15)`,
                color: colors.hex2 || colors.text,
                border: `1.5px solid ${colors.border}`,
                boxShadow: `0 0 20px ${colors.hex}15, 0 4px 12px rgba(0,0,0,0.1)`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.hex}35, ${(colors.hex2 || colors.hex)}25)`;
                e.currentTarget.style.boxShadow = `0 0 30px ${colors.hex}30, 0 6px 20px rgba(0,0,0,0.15)`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.hex}20, ${(colors.hex2 || colors.hex)}15)`;
                e.currentTarget.style.boxShadow = `0 0 20px ${colors.hex}15, 0 4px 12px rgba(0,0,0,0.1)`;
              }}
            >
              {pt.cta}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
      <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-48 h-48 rounded-full blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-700" style={{ background: hasGradient ? `linear-gradient(135deg, ${colors.hex}, ${colors.hex2})` : colors.hex }} />
    </div>
  );
}

export default function Products() {
  const { lang } = useLang();
  const t = translations[lang].products;
  const [showPresentation, setShowPresentation] = useState(false);

  return (
    <section data-testid="products-section" id="products" className="py-20 sm:py-24 lg:py-28 relative">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-64 h-64 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl" />
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full dark:bg-cyan-500/10 bg-cyan-50 border dark:border-cyan-500/20 border-cyan-200 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-cyan-500">{t.badge}</span>
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4"><AnimatedGradientText>{t.title}</AnimatedGradientText></h2>
          <p className="text-base dark:text-white/50 text-slate-500 max-w-lg mx-auto">{t.desc}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          <ProductCard productKey="crm" color="cyan" status="live" t={t} link="https://crm.whowsell.com/" onShowPresentation={() => setShowPresentation(true)} />
          <ProductCard productKey="radar" color="radarGradient" status="live" t={t} link="https://radar.whowsell.com/" />
          <ProductCard productKey="ai" color="aiGradient" status="beta" t={t} link="https://ai.whowsell.com/" />
          <ProductCard productKey="cloud" color="cloudGradient" status="live" t={t} link="https://server.whowsell.com/" />
        </div>
      </div>

      <CrmPresentationModal isOpen={showPresentation} onClose={() => setShowPresentation(false)} />
    </section>
  );
}
